import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Pagination, usePagedList, useEmptyPageGuard, useDebounced, useLatestRequest,
} from '@/components/ui/pagination';
import { StatCard, Fact, toneOf } from '@/components/ui/stat';
import { money, pct, ratio, fixed, duration, price, dateOnly } from '@/lib/format';
import {
  SearchInput, FilterChips, SortControl, SortableHeader, EmptyState,
  type FilterOption,
} from '@/components/ui/list-toolbar';
import {
  automationService,
  type Strategy,
  type RuleStatistics,
  type StrategyTrade,
  type RuleSortBy,
  type SortDir,
  type TradeSortBy,
  tradeChartable,
} from '@/services/strategyService';
import { scannerApiService } from '@/services/scannerService';
import { RuleFormDialog } from './RuleFormDialog';
import { SpecHistoryDialog } from './SpecHistoryDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { TradeAuditDetail } from './TradeAuditDetail';
import { EquityCurveChart, PnLDistributionChart, MfeScatterPlot } from './PerformanceCharts';
import {
  Plus, Play, Pause, Trash2, Edit, History, TrendingUp, Target, RefreshCw,
  Loader2, Radio, FlaskConical, Inbox, SearchX, ArrowLeft, Wallet, CandlestickChart,
} from 'lucide-react';
import { paperAccountService } from '@/services/paperAccountService';
import { TradeChartDialog } from '@/components/trading/TradeChartDialog';

// Formatting lives in @/lib/format so the same number reads the same way on every
// screen; only the page-specific vocabulary is declared here.

type StatusFilter = 'all' | 'active' | 'inactive';

const STATUS_OPTIONS: FilterOption<StatusFilter>[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active', activeClass: 'bg-emerald-500 text-white' },
  { value: 'inactive', label: 'Inactive', activeClass: 'bg-muted-foreground/80 text-background' },
];

const ASSET_OPTIONS: FilterOption<string>[] = [
  { value: 'all', label: 'All assets' },
  { value: 'Equity', label: 'Equity' },
  { value: 'Forex', label: 'Forex' },
  { value: 'Crypto', label: 'Crypto' },
];

const SORT_OPTIONS: { value: RuleSortBy; label: string }[] = [
  { value: 'createdAt', label: 'Created' },
  { value: 'updatedAt', label: 'Last updated' },
  { value: 'name', label: 'Name' },
  { value: 'enabled', label: 'Status' },
];

// ── Main Page Component ────────────────────────────────────

/** One column on a phone, filling out to four on a wide desktop. */
const GRID = 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4';

export const StrategiesPage: React.FC = () => {
  const [rules, setRules] = useState<Strategy[]>([]);
  const [total, setTotal] = useState(0);
  // The SELECTED strategy is held as an object, not looked up in `rules`. Deriving it
  // from the current page meant changing page or filter blanked the whole right-hand
  // panel, because the selected rule was simply no longer in the loaded slice.
  const [selectedRule, setSelectedRule] = useState<Strategy | null>(null);
  const selectedRuleId = selectedRule?.id ?? null;
  const [stats, setStats] = useState<Record<string, RuleStatistics>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scannerNames, setScannerNames] = useState<Record<string, string>>({});
  const [accountNames, setAccountNames] = useState<Record<string, string>>({});
  const [formOpen, setFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Strategy | null>(null);
  const [historyRule, setHistoryRule] = useState<Strategy | null>(null);
  const [busyRuleId, setBusyRuleId] = useState<string | null>(null);

  // ── Server-side list state ──
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [assetClass, setAssetClass] = useState<string>('all');
  const [sortBy, setSortBy] = useState<RuleSortBy>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const debouncedSearch = useDebounced(search, 300);
  // The hook owns the "any filter change resets to page 1" rule.
  const { page, setPage, pageSize, setPageSize } = usePagedList({
    pageSize: 25,
    resetOn: [debouncedSearch, status, assetClass, sortBy, sortDir],
  });

  const beginRequest = useLatestRequest();

  const loadRules = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      setLoading(true);
      const result = await automationService.getRulesPage({
        page,
        pageSize,
        sortBy,
        sortDir,
        search: debouncedSearch || undefined,
        enabled: status === 'all' ? undefined : status === 'active',
        assetClass: assetClass === 'all' ? undefined : assetClass,
      });
      // A superseded response must not land: it would show the previous page's rows
      // under the current page number, and re-pin an older copy of the selected rule
      // over the one being edited.
      if (!isCurrent()) return;
      setRules(result.items);
      setTotal(result.total ?? result.items.length);
      // Refresh the pinned selection from the new page when it is present, so an edit
      // or a toggle made elsewhere shows up without dropping the selection.
      setSelectedRule((cur) => (cur ? result.items.find((r) => r.id === cur.id) ?? cur : cur));
      setError(null);
    } catch (e) {
      if (!isCurrent()) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [page, pageSize, sortBy, sortDir, debouncedSearch, status, assetClass, beginRequest]);

  useEffect(() => { loadRules(); }, [loadRules]);

  // Deleting the last rule on a page would otherwise leave it stranded on an empty
  // page showing the "no strategies yet" message.
  useEmptyPageGuard(page, setPage, rules.length, total);

  // Scanner names are a small, slow-changing lookup — fetched once, not per page.
  useEffect(() => {
    scannerApiService.getScanners()
      .then((scanners) => {
        const nameMap: Record<string, string> = {};
        scanners.forEach((s) => { nameMap[s.id] = s.name; });
        setScannerNames(nameMap);
      })
      .catch(() => { /* names are cosmetic; the id still renders */ });
  }, []);

  // Paper-account names, same shape: the card shows WHICH pot each strategy spends
  // from — previously only visible by opening the edit dialog.
  useEffect(() => {
    paperAccountService.list()
      .then((accounts) => {
        const nameMap: Record<string, string> = {};
        accounts.forEach((a) => { nameMap[a.accountId] = a.name; });
        setAccountNames(nameMap);
      })
      .catch(() => { /* cosmetic; the chip falls back to the raw id */ });
  }, []);

  const loadStats = useCallback(async (ruleId: string) => {
    try {
      const s = await automationService.getRuleStatistics(ruleId);
      setStats((prev) => ({ ...prev, [ruleId]: s }));
    } catch (e) {
      console.error('Failed to load stats', e);
    }
  }, []);

  useEffect(() => {
    if (selectedRuleId) loadStats(selectedRuleId);
  }, [selectedRuleId, loadStats]);

  const toggleRule = async (rule: Strategy) => {
    setBusyRuleId(rule.id);
    try {
      if (rule.enabled) await automationService.disableRule(rule.id);
      else await automationService.enableRule(rule.id);
      // Optimistic flip so the card recolours instantly; the reload confirms it.
      const flip = (r: Strategy) => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r);
      setRules((prev) => prev.map(flip));
      setSelectedRule((cur) => (cur ? flip(cur) : cur));
      await loadRules();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await loadRules();
    } finally {
      setBusyRuleId(null);
    }
  };

  const deleteRule = async (ruleId: string) => {
    if (!confirm('Delete this rule?')) return;
    try {
      await automationService.deleteRule(ruleId);
      if (selectedRuleId === ruleId) setSelectedRule(null);
      loadRules();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const openNewRule = () => { setEditingRule(null); setFormOpen(true); };
  const openEditRule = (rule: Strategy) => { setEditingRule(rule); setFormOpen(true); };

  const selectedStats = selectedRuleId ? stats[selectedRuleId] : null;

  const activeCount = useMemo(() => rules.filter((r) => r.enabled).length, [rules]);
  const filtersActive = debouncedSearch !== '' || status !== 'all' || assetClass !== 'all';

  const pager = (compact: boolean) => (
    <Pagination
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={setPage}
      onPageSizeChange={compact ? undefined : setPageSize}
      pageSizeOptions={[10, 25, 50, 100]}
      compact={compact}
      loading={loading}
      itemLabel="strategies"
    />
  );

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Target className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold leading-tight">Trading Rules</h2>
            <span className="text-xs text-muted-foreground">
              Rules that trade your scanners automatically — paper first, live when proven
              {total > 0 && <> · <span className="tabular-nums">{total.toLocaleString()}</span> total
                {activeCount > 0 && <> · <span className="text-emerald-500">{activeCount} active on this page</span></>}
              </>}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadRules} disabled={loading}>
            {loading
              ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            Refresh
          </Button>
          <Button size="sm" onClick={openNewRule}>
            <Plus className="mr-1 h-3.5 w-3.5" /> New Rule
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}

      {/* ── Filters ── */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search strategies by name…"
            className="w-full sm:w-64"
          />
          <FilterChips value={status} options={STATUS_OPTIONS} onChange={(v) => setStatus(v)} />
          <FilterChips value={assetClass} options={ASSET_OPTIONS} onChange={(v) => setAssetClass(v)} />
          <SortControl
            sortBy={sortBy}
            sortDir={sortDir}
            options={SORT_OPTIONS}
            onChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
            className="ml-auto"
          />
        </CardContent>
      </Card>

      {/* The strategies themselves get the full width. Detail lives in a dialog
          (below) rather than a permanent side panel: the panel was empty until
          something was picked, and it cost two thirds of the page to say so. */}
      <div className="space-y-3">
          {/* Top pager — a long list shouldn't need a scroll back up to change page. */}
          {total > pageSize && <div className="px-0.5">{pager(true)}</div>}

          {loading && rules.length === 0 && (
            <div className={GRID}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-[176px] animate-pulse rounded-lg border bg-muted/30" />
              ))}
            </div>
          )}

          {rules.length > 0 && (
            <div className={GRID}>
              {rules.map((rule) => (
                <StrategyCard
                  key={rule.id}
                  rule={rule}
                  selected={selectedRuleId === rule.id}
                  busy={busyRuleId === rule.id}
                  scannerName={scannerNames[rule.scannerId]}
                  paperAccountName={rule.paperAccountId ? (accountNames[rule.paperAccountId] ?? rule.paperAccountId) : undefined}
                  onSelect={() => setSelectedRule(rule)}
                  onEdit={() => openEditRule(rule)}
                  onHistory={() => setHistoryRule(rule)}
                  onToggle={() => toggleRule(rule)}
                  onDelete={() => deleteRule(rule.id)}
                />
              ))}
            </div>
          )}

          {!loading && rules.length === 0 && (
            <Card>
              {filtersActive ? (
                <EmptyState
                  icon={<SearchX className="h-8 w-8" />}
                  title="No strategies match these filters"
                  hint="Try clearing the search box or widening the status and asset filters."
                  action={
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSearch(''); setStatus('all'); setAssetClass('all'); }}
                    >
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<Inbox className="h-8 w-8" />}
                  title="A trading rule trades a scanner automatically."
                  hint={
                    <>
                      Pick one of your scanners, set position sizing and exits, and the rule
                      paper-trades every hit — no broker account needed. Prove it in{' '}
                      <a href="#/dashboard/scenarios" className="text-primary underline">Backtests</a>{' '}
                      before letting it run live, or ask the{' '}
                      <a href="#/dashboard/ai-assistant" className="text-primary underline">AI assistant</a>{' '}
                      to build one for you.
                    </>
                  }
                  action={<Button size="sm" onClick={openNewRule}><Plus className="mr-1 h-3.5 w-3.5" /> New Rule</Button>}
                />
              )}
            </Card>
          )}

          {/* Bottom pager — carries the page-size picker. */}
          {total > 0 && <div className="px-0.5 pt-1">{pager(false)}</div>}
        </div>

      {/* Strategy detail, on tile click. */}
      <Dialog open={!!selectedRule} onOpenChange={(open) => { if (!open) setSelectedRule(null); }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6">
              <span className="truncate">{selectedRule?.name ?? 'Strategy'}</span>
              {selectedRule && (
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  {selectedRule.tradingEnvironment === 'Live' ? 'Live' : 'Paper'}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedRule && selectedStats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatCard label="Total Trades" value={selectedStats.totalTrades.toLocaleString()} />
                <StatCard label="Win Rate" value={pct(selectedStats.winRate)} tone={toneOf(selectedStats.winRate, 50)} />
                <StatCard label="Total P&L" value={money(selectedStats.totalPnL)} tone={toneOf(selectedStats.totalPnL)} />
                <StatCard label="Profit Factor" value={ratio(selectedStats.profitFactor)} tone={toneOf(selectedStats.profitFactor, 1)} />
                <StatCard label="Expectancy" value={money(selectedStats.expectancy)} tone={toneOf(selectedStats.expectancy)} />
                <StatCard label="Avg R-Multiple" value={fixed(selectedStats.avgRMultiple)} tone={toneOf(selectedStats.avgRMultiple)} />
                {/* Drawdown is always red: it is a cost, never a gain, so toneOf would be wrong here. */}
                <StatCard label="Max Drawdown" value={money(-selectedStats.maxDrawdown)} tone="bad" />
                <StatCard label="Avg Hold Time" value={duration(selectedStats.avgHoldingSeconds)} />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Card>
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-sm">Best & Worst</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 p-3 pt-1 text-xs">
                    <div className="text-emerald-500">Largest Win: {money(selectedStats.largestWin)}</div>
                    <div className="text-emerald-500">Avg Win: {money(selectedStats.avgWin)}</div>
                    <div className="text-red-500">Largest Loss: {money(selectedStats.largestLoss)}</div>
                    <div className="text-red-500">Avg Loss: {money(selectedStats.avgLoss)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-sm">Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 p-3 pt-1 text-xs">
                    <div className="text-emerald-500">Winners: {selectedStats.winners.toLocaleString()}</div>
                    <div className="text-red-500">Losers: {selectedStats.losers.toLocaleString()}</div>
                    <div>Breakeven: {selectedStats.breakeven}</div>
                    <div>Open: {selectedStats.openTrades}</div>
                  </CardContent>
                </Card>
              </div>

              {selectedStats.equityCurve.length > 0 && (
                <EquityCurveChart equityCurve={selectedStats.equityCurve} />
              )}

              <StrategyTradesPanel ruleId={selectedRule.id} assetClass={selectedRule.assetClass || 'Equity'} />
            </div>
          ) : (
            <EmptyState
              icon={<Loader2 className="h-8 w-8 animate-spin" />}
              title="Loading statistics…"
            />
          )}
        </DialogContent>
      </Dialog>

      <RuleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editRule={editingRule}
        onSaved={loadRules}
      />

      {historyRule && (
        <SpecHistoryDialog
          open={!!historyRule}
          onOpenChange={(open) => { if (!open) setHistoryRule(null); }}
          ruleId={historyRule.id}
          ruleName={historyRule.name}
        />
      )}
    </div>
  );
};

// ── Strategy Card ──────────────────────────────────────────

/**
 * Enabled state is carried by COLOUR, not just a badge: an armed strategy is placing
 * real orders, and that should be readable at a glance while scanning a list. Live
 * (real-money) gets its own treatment again — the difference between paper and live
 * is the most consequential fact on the card.
 */
const StrategyCard: React.FC<{
  rule: Strategy;
  selected: boolean;
  busy: boolean;
  scannerName?: string;
  paperAccountName?: string;
  onSelect: () => void;
  onEdit: () => void;
  onHistory: () => void;
  onToggle: () => void;
  onDelete: () => void;
}> = ({ rule, selected, busy, scannerName, paperAccountName, onSelect, onEdit, onHistory, onToggle, onDelete }) => {
  const isLive = rule.tradingEnvironment === 'Live';
  const active = rule.enabled;

  const tone = active
    ? isLive
      ? 'border-l-amber-500 bg-amber-500/[0.04]'
      : 'border-l-emerald-500 bg-emerald-500/[0.04]'
    : 'border-l-transparent opacity-75';

  return (
    <Card
      onClick={onSelect}
      className={`cursor-pointer border-l-[3px] transition-all hover:shadow-sm ${tone} ${
        selected ? 'ring-1 ring-primary border-primary/40' : 'hover:border-muted-foreground/30'
      }`}
    >
      <CardContent className="space-y-2.5 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-1.5">
              {/* A pulsing dot reads as "running" faster than any word does. */}
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                active ? (isLive ? 'animate-pulse bg-amber-500' : 'animate-pulse bg-emerald-500') : 'bg-muted-foreground/40'
              }`} />
              <span className="truncate text-sm font-semibold">{rule.name}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <Badge
                variant={active ? 'default' : 'secondary'}
                className={`text-[10px] ${active && !isLive ? 'bg-emerald-500 hover:bg-emerald-500' : ''} ${
                  active && isLive ? 'bg-amber-500 hover:bg-amber-500 text-black' : ''
                }`}
              >
                {active ? 'Active' : 'Inactive'}
              </Badge>
              <Badge variant="outline" className="gap-1 text-[10px]">
                {isLive
                  ? <><Radio className="h-2.5 w-2.5" /> Live</>
                  : <><FlaskConical className="h-2.5 w-2.5" /> Paper</>}
              </Badge>
              <Badge variant="outline" className="text-[10px]">{rule.assetClass || 'Equity'}</Badge>
              <Badge variant="outline" className={`text-[10px] ${
                rule.direction === 'Long' ? 'text-emerald-500' : 'text-red-500'
              }`}>
                {rule.direction === 'Long' ? '▲' : '▼'} {rule.direction}
              </Badge>
              {/* Which pot this strategy spends from. Unbound strategies share the
                  default pot — say so rather than showing nothing, since "shares a
                  balance with every other unbound strategy" is exactly the state
                  worth noticing at a glance. Live rules spend from their BROKER, not
                  any paper pot (promotion retains the old binding), so no wallet —
                  labeling real-money trading with a paper pot would be a lie. */}
              {!isLive && (
                <Badge variant="outline" className="gap-1 text-[10px] text-muted-foreground max-w-36">
                  <Wallet className="h-2.5 w-2.5 shrink-0" />
                  <span className="truncate">{paperAccountName ?? 'Shared account'}</span>
                </Badge>
              )}
            </div>
          </div>

          <div className="flex shrink-0 gap-0.5">
            <IconButton title="Edit" onClick={onEdit}><Edit className="h-3.5 w-3.5" /></IconButton>
            <IconButton title="Version history" onClick={onHistory}><History className="h-3.5 w-3.5" /></IconButton>
            <IconButton
              title={active ? 'Pause' : 'Activate'}
              onClick={onToggle}
              className={active ? 'text-amber-500' : 'text-emerald-500'}
            >
              {busy
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            </IconButton>
            <IconButton title="Delete" onClick={onDelete} className="text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </IconButton>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
          <Fact label="Scanner" value={scannerName || rule.scannerId} />
          <Fact label="Max open" value={`${rule.maxConcurrentTrades} trades`} />
          <Fact
            label="Sizing"
            value={`${rule.sizing.mode} ${rule.sizing.value}${rule.sizing.mode === 'RiskPercent' ? '%' : ''}`}
          />
          <Fact
            label="Stop"
            value={rule.exits.stopLoss ? `${rule.exits.stopLoss.mode} ×${rule.exits.stopLoss.value}` : '—'}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const IconButton: React.FC<{
  title: string;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}> = ({ title, onClick, className = '', children }) => (
  <Button
    variant="ghost"
    size="sm"
    title={title}
    className={`h-7 w-7 p-0 ${className}`}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
  >
    {children}
  </Button>
);

// ── Trades panel (server-paged) ────────────────────────────

type TradeStatusFilter = 'all' | 'Open' | 'Closed';
type TradeOutcomeFilter = 'all' | 'win' | 'loss';

const TRADE_STATUS_OPTIONS: FilterOption<TradeStatusFilter>[] = [
  { value: 'all', label: 'All' },
  { value: 'Open', label: 'Open', activeClass: 'bg-blue-500 text-white' },
  { value: 'Closed', label: 'Closed' },
];

const TRADE_OUTCOME_OPTIONS: FilterOption<TradeOutcomeFilter>[] = [
  { value: 'all', label: 'Any' },
  { value: 'win', label: 'Wins', activeClass: 'bg-emerald-500 text-white' },
  { value: 'loss', label: 'Losses', activeClass: 'bg-red-500 text-white' },
];

/**
 * The strategy's trade history, paged and sorted by the SERVER.
 *
 * This reads the persisted documents rather than the in-memory performance grain, so
 * it shows the whole history rather than only trades since the last silo restart —
 * the distinction the paged endpoint exists to honour.
 */
const StrategyTradesPanel: React.FC<{ ruleId: string; assetClass: string }> = ({ ruleId, assetClass }) => {
  const [trades, setTrades] = useState<StrategyTrade[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTradeId, setExpandedTradeId] = useState<string | null>(null);
  // A row click expands the audit (signal + events); the chart is its own
  // affordance per row — same TradeChartDialog the Backtest Lab uses, so the
  // stepped stop line and event panel render identically for live trades.
  const [chartTrade, setChartTrade] = useState<StrategyTrade | null>(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TradeStatusFilter>('all');
  const [outcome, setOutcome] = useState<TradeOutcomeFilter>('all');
  const [sortBy, setSortBy] = useState<TradeSortBy>('entryTime');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const debouncedSearch = useDebounced(search, 300);
  // ruleId is a reset key too — switching strategy must not keep page 4 of the
  // previous one's history.
  const { page, setPage, pageSize, setPageSize } = usePagedList({
    pageSize: 25,
    resetOn: [ruleId, debouncedSearch, status, outcome, sortBy, sortDir],
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    automationService.getRuleTradesPage(ruleId, {
      page, pageSize, sortBy, sortDir,
      search: debouncedSearch || undefined,
      status: status === 'all' ? undefined : status,
      outcome: outcome === 'all' ? undefined : outcome,
    })
      .then((result) => {
        if (cancelled) return;
        setTrades(result.items);
        setTotal(result.total ?? result.items.length);
        setError(null);
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load trades'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ruleId, page, pageSize, sortBy, sortDir, debouncedSearch, status, outcome]);

  const toggleSort = (column: TradeSortBy) => {
    if (sortBy === column) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortDir('desc'); }
  };

  const closedTrades = useMemo(() => trades.filter((t) => t.status === 'Closed'), [trades]);

  const pager = (compact: boolean) => (
    <Pagination
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={setPage}
      onPageSizeChange={compact ? undefined : setPageSize}
      compact={compact}
      loading={loading}
      itemLabel="trades"
    />
  );

  return (
    <>
      {/* These charts describe the CURRENT PAGE of trades, not the whole history — so
          the page says so. An unlabelled 25-trade distribution reads as the strategy's
          distribution, and that is a materially different claim. */}
      {closedTrades.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Distribution of the {closedTrades.length} closed trade{closedTrades.length === 1 ? '' : 's'} on this page
            {total > pageSize && <> · use the pager or a wider page size to widen the sample</>}
          </div>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <PnLDistributionChart trades={closedTrades} />
            <MfeScatterPlot trades={closedTrades} />
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="space-y-2 p-3 pb-2">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="flex items-center gap-1.5 text-sm">
              <TrendingUp className="h-3.5 w-3.5 text-primary" />
              Trades
              {total > 0 && <span className="font-normal text-muted-foreground">({total.toLocaleString()})</span>}
            </CardTitle>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Symbol or reason…"
              className="w-40 sm:w-52"
            />
            <FilterChips value={status} options={TRADE_STATUS_OPTIONS} onChange={(v) => setStatus(v)} />
            <FilterChips value={outcome} options={TRADE_OUTCOME_OPTIONS} onChange={(v) => setOutcome(v)} />
          </div>
          {total > pageSize && pager(true)}
        </CardHeader>

        <CardContent className="p-0">
          {error && <div className="p-3 text-xs text-red-500">{error}</div>}

          {!error && trades.length === 0 && !loading && (
            <EmptyState
              icon={<Inbox className="h-7 w-7" />}
              title={total === 0 && !search && status === 'all' && outcome === 'all'
                ? 'No trades yet'
                : 'No trades match these filters'}
              hint={total === 0 && !search
                ? 'Trades appear here once the strategy is active and its scanner fires.'
                : undefined}
            />
          )}

          {trades.length > 0 && (
            <div className="overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-muted/60 backdrop-blur">
                  <tr className="text-[10px] uppercase tracking-wider">
                    <th className="w-4 p-2" />
                    <th className="p-2 text-left"><SortableHeader column="symbol" label="Symbol" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} /></th>
                    <th className="p-2 text-left"><SortableHeader column="direction" label="Dir" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} /></th>
                    <th className="p-2 text-left"><SortableHeader column="entryTime" label="Entry" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} /></th>
                    <th className="p-2 text-right"><SortableHeader column="entryPrice" label="Entry px" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" /></th>
                    <th className="p-2 text-right"><SortableHeader column="exitPrice" label="Exit px" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" /></th>
                    <th className="p-2 text-right"><SortableHeader column="realizedPnL" label="P&L" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" /></th>
                    <th className="p-2 text-right"><SortableHeader column="realizedRMultiple" label="R" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" /></th>
                    <th className="p-2 text-left"><SortableHeader column="exitReason" label="Reason" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} /></th>
                    <th className="p-2 text-right"><SortableHeader column="holdingSeconds" label="Hold" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" /></th>
                    <th className="w-8 p-2" />
                  </tr>
                </thead>
                <tbody className={loading ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
                  {trades.map((t) => {
                    const expanded = expandedTradeId === t.id;
                    const isOpen = t.status === 'Open';
                    const win = t.realizedPnL > 0;
                    return (
                      <React.Fragment key={t.id}>
                        <tr
                          className={`cursor-pointer border-b border-muted/20 transition-colors hover:bg-muted/20 ${
                            expanded ? 'bg-muted/25' : ''
                          }`}
                          onClick={() => setExpandedTradeId(expanded ? null : t.id)}
                          title="Show entry signal + exit audit"
                        >
                          <td className="select-none p-2 text-muted-foreground">{expanded ? '▾' : '▸'}</td>
                          <td className="p-2 font-medium">{t.symbol}</td>
                          <td className={`p-2 ${t.direction === 'Long' ? 'text-emerald-500' : 'text-red-500'}`}>
                            {t.direction === 'Long' ? '▲' : '▼'}
                          </td>
                          <td className="whitespace-nowrap p-2 text-muted-foreground">{dateOnly(t.entryTime)}</td>
                          <td className="p-2 text-right tabular-nums">{price(t.entryPrice, t.symbol)}</td>
                          <td className="p-2 text-right tabular-nums">
                            {isOpen
                              ? <span className="text-blue-400">{t.lastPrice > 0 ? price(t.lastPrice, t.symbol) : 'open'}</span>
                              : price(t.exitPrice, t.symbol)}
                          </td>
                          <td className={`p-2 text-right tabular-nums font-medium ${
                            isOpen ? 'text-muted-foreground' : win ? 'text-emerald-500' : 'text-red-500'
                          }`}>
                            {isOpen ? '—' : money(t.realizedPnL)}
                          </td>
                          <td className="p-2 text-right tabular-nums">
                            {isOpen ? '—' : fixed(t.realizedRMultiple, 1)}
                          </td>
                          <td className="p-2">
                            {isOpen
                              ? <span className="rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[10px] text-blue-400">Open</span>
                              : <span className="text-muted-foreground">{t.exitReason || '—'}</span>}
                          </td>
                          <td className="p-2 text-right tabular-nums text-muted-foreground">
                            {t.holdingSeconds > 0 ? duration(t.holdingSeconds) : '—'}
                          </td>
                          <td className="p-2">
                            {/* Chart only statuses that OWN a position — an allowlist, not
                                a Cancelled-exclusion: EntryPending has no fill either, and
                                the dialog treats "no exitTime" as open-until-today. */}
                            {tradeChartable(t) && (
                              <button
                                type="button"
                                title="View on chart"
                                className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                                onClick={(e) => { e.stopPropagation(); setChartTrade(t); }}
                              >
                                <CandlestickChart className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="border-b border-muted/20 bg-muted/10">
                            <td colSpan={11} className="p-3">
                              <TradeAuditDetail trade={t} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 && <div className="border-t p-3">{pager(false)}</div>}
        </CardContent>
      </Card>

      {/* The trade's own write-once assetClass wins (editing the rule's class later
          must not re-render old trades' history); the rule value covers pre-field
          legacy trades. The chart picks bar source + price precision from it. */}
      <TradeChartDialog
        trade={chartTrade ? ({ ...chartTrade, assetClass: chartTrade.assetClass ?? assetClass } as any) : null}
        events={chartTrade?.events}
        entryReason={chartTrade?.entryReason ?? null}
        onClose={() => setChartTrade(null)}
      />
    </>
  );
};
